/**
 * The ONE editor setup for the advisor summary. The admin's editor, the
 * portal block and the printed report all build from this list, so what is
 * shown is exactly what was written — and nothing outside this schema (raw
 * HTML, scripts, unknown tags) can ever be rendered: ProseMirror drops what the
 * schema does not know, and the server refuses it before it is stored
 * (advisorDocSchema in @agio/core).
 */
import type { Extensions } from '@tiptap/react';
import StarterKit from '@tiptap/starter-kit';
import Link from '@tiptap/extension-link';
import { TextStyle, Color } from '@tiptap/extension-text-style';
import { Table, TableRow, TableHeader, TableCell } from '@tiptap/extension-table';
import TextAlign from '@tiptap/extension-text-align';
import { ADVISOR_PALETTE, isSafeHref, toPaletteColor } from '@agio/core/domain';

/**
 * Links: http, https and mailto only; always a new tab, and always without a
 * handle back to the portal — whatever attributes the stored link carries.
 */
const SafeLink = Link.extend({
  addAttributes() {
    return {
      ...this.parent?.(),
      // Fixed values, also for a link pasted in from elsewhere (which would
      // otherwise bring its own target, rel and class along).
      target: { default: '_blank', parseHTML: () => '_blank', renderHTML: () => ({ target: '_blank' }) },
      rel: { default: 'noopener noreferrer', parseHTML: () => 'noopener noreferrer', renderHTML: () => ({ rel: 'noopener noreferrer' }) },
      class: { default: null, parseHTML: () => null, renderHTML: () => ({}) },
    };
  },
});

/**
 * Text colour, limited to the palette. Pasted text keeps a colour only when it
 * is one of ours (the browser hands it over as rgb(), which is mapped back);
 * any other colour is dropped rather than stored.
 */
const PaletteColor = Color.extend({
  addGlobalAttributes() {
    return [
      {
        types: ['textStyle'],
        attributes: {
          color: {
            default: null,
            parseHTML: (element: HTMLElement) => toPaletteColor(element.style.color),
            renderHTML: (attributes: { color?: unknown }) => {
              const color = toPaletteColor(attributes.color);
              return color ? { style: `color: ${color}` } : {};
            },
          },
        },
      },
    ];
  },
});

export function advisorExtensions({ editable }: { editable: boolean }): Extensions {
  return [
    StarterKit.configure({
      // The extended link below replaces StarterKit's own.
      link: false,
      heading: { levels: [1, 2, 3] },
      // The editor keeps an empty line after a table so you can type past it;
      // the read-only text must not grow one.
      ...(editable ? {} : { trailingNode: false as const }),
    }),
    SafeLink.configure({
      openOnClick: !editable,
      autolink: true,
      defaultProtocol: 'https',
      protocols: ['mailto'],
      isAllowedUri: (url) => isSafeHref(url),
      shouldAutoLink: (url) => isSafeHref(url.includes(':') ? url : `https://${url}`),
    }),
    TextStyle,
    PaletteColor,
    Table.configure({ resizable: false }),
    TableRow,
    TableHeader,
    TableCell,
    // Hebrew text starts on the right; nothing is stored until the admin picks an alignment.
    TextAlign.configure({ types: ['heading', 'paragraph'], alignments: ['right', 'center', 'left', 'justify'] }),
  ];
}

/** The text colours the toolbar offers (re-shaded on light backgrounds in styles.css). */
export const ADVISOR_COLORS = ADVISOR_PALETTE;
